<template>
  <div class="zw zw-water-usage">
    <div class="ui basic padded segment">

      <div class="ui header">
        <i class="icon chart line"></i>
        {{ $t('pages.water_usage.title') }}
      </div>

      <div class="ui segment zw-water-usage-filters">
        <div class="ui form">
          <div class="three fields zw-daterange">
            <div class="field">
              <label>{{ $t('pages.water_usage.filter.from') }}</label>
              <div class="ui input">
                <flat-pickr
                  v-model="from"
                  :config="fromConfig"
                  placeholder="YYYY-MM-DD"
                />
              </div>
            </div>

            <div class="field">
              <label>{{ $t('pages.water_usage.filter.to') }}</label>
              <div class="ui input">
                <flat-pickr
                  v-model="to"
                  :config="toConfig"
                  placeholder="YYYY-MM-DD"
                />
              </div>
            </div>

            <div class="field" v-if="kamstrupUsers.length > 1">
              <label>{{ $t('pages.water_usage.filter.house_meter') }}</label>
              <select
                v-model="selectedKamstrupUserId"
                class="ui dropdown zw-meter-select"
                @change="page = 1; fetch()"
              >
                <option
                  v-for="item in kamstrupUsers"
                  :key="item.id"
                  :value="item.id"
                >
                  {{ item.username }}
                  - {{ item.address || '-' }}, {{ item.city || '-' }}
                  - {{ item.user_number || '-' }}
                  {{ item.meter_number ? ` (${item.meter_number})` : '' }}
                  {{ item.is_current_unpaid ? ' - Aktuel' : '' }}
                </option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div class="ui labels zw-water-usage-labels" v-if="meta">
        <div class="ui label" v-if="selectedKamstrupUser">
          <i class="home icon"></i>
          {{ selectedHouseText }}
        </div>

        <div class="ui label">
          <i class="barcode icon"></i>
          {{ $t('pages.water_usage.meter_serial_number') }}
          <div class="detail">
            {{ meterSerial }}
          </div>
        </div>

        <div class="ui label">
          <i class="calendar icon"></i>
          {{ rangeText }}
        </div>
      </div>

      <table class="ui celled table">
        <thead>
        <tr>
          <th>{{ $t('pages.water_usage.table.date') }}</th>
          <th>{{ $t('pages.water_usage.table.start_meter') }}</th>
          <th>{{ $t('pages.water_usage.table.end_meter') }}</th>
          <th class="right aligned">{{ $t('pages.water_usage.table.total') }}</th>
          <th>{{ $t('pages.water_usage.table.info_codes') }}</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="row in rows" :key="row.day">
          <td>
            <button
              type="button"
              class="zw-day-link"
              @click="openHourlyModal(row.day)"
            >
              {{ row.day }}
            </button>
          </td>
          <td>{{ row.start_volume }}</td>
          <td>{{ row.end_volume }}</td>
          <td class="right aligned">{{ row.used_volume }}</td>
          <td>{{ row.info_codes || '-' }}</td>
        </tr>
        </tbody>
      </table>

      <div class="zw-paginate">
        <div class="ui pagination menu">
          <a class="item" :class="{ disabled: page === 1 }" @click="go(page-1)">
            {{ $t('pages.water_usage.table.pagination.prev') }}
          </a>
          <a
            class="item"
            v-for="p in totalPages"
            :key="p"
            :class="{ active: p === page }"
            @click="go(p)"
          >
            {{ p }}
          </a>
          <a class="item" :class="{ disabled: page === totalPages }" @click="go(page+1)">
            {{ $t('pages.water_usage.table.pagination.next') }}
          </a>
        </div>
      </div>

      <div
        v-if="hourlyModal.open"
        class="zw-water-usage-modal-backdrop"
        @click.self="closeHourlyModal"
      >
        <div class="zw-water-usage-modal ui segment">
          <div class="zw-water-usage-modal-header">
            <h4 class="ui header">
              {{ $t('pages.water_usage.hourly.title') }} - {{ hourlyModal.day }}
            </h4>
            <button type="button" class="ui icon button" @click="closeHourlyModal">
              <i class="close icon"></i>
            </button>
          </div>

          <div class="ui labels" v-if="hourlySelectedKamstrupUser">
            <div class="ui label">
              <i class="home icon"></i>
              {{ hourlySelectedHouseText }}
            </div>
            <div class="ui label">
              <i class="barcode icon"></i>
              {{ $t('pages.water_usage.meter_serial_number') }}
              <div class="detail">{{ hourlyMeterSerial }}</div>
            </div>
          </div>

          <div v-if="hourlyModal.loading" class="ui active inline loader"></div>

          <table v-else class="ui celled table">
            <thead>
            <tr>
              <th>{{ $t('pages.water_usage.table.date') }}</th>
              <th class="right aligned">{{ $t('pages.water_usage.table.total') }}</th>
              <th>{{ $t('pages.water_usage.table.info_codes') }}</th>
            </tr>
            </thead>
            <tbody>
            <tr v-if="!hourlyModal.rows.length">
              <td colspan="3">{{ $t('pages.water_usage.hourly.no_data') }}</td>
            </tr>
            <tr v-for="row in hourlyModal.rows" :key="row.datetime">
              <td>{{ row.datetime }}</td>
              <td class="right aligned">{{ row.used_volume }}</td>
              <td>{{ row.info_codes || '-' }}</td>
            </tr>
            </tbody>
            <tfoot v-if="hourlyModal.rows.length">
            <tr>
              <th class="right aligned" colspan="2">{{ $t('pages.water_usage.hourly.total_per_day') }}</th>
              <th class="right aligned">{{ formatVolume(hourlyTotalUsed) }}</th>
            </tr>
            </tfoot>
          </table>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import flatPickr from 'vue-flatpickr-component';
import 'flatpickr/dist/flatpickr.css';

export default {
  name: 'WaterUsagePage',
  components: { flatPickr },

  data() {
    const now = new Date();
    const from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const to = now;

    return {
      from,
      to,
      page: 1,
      perPage: 15,
      totalPages: 1,
      meta: null,
      rows: [],
      loading: false,
      selectedKamstrupUserId: null,
      hourlyModal: {
        open: false,
        loading: false,
        day: null,
        rows: [],
        meta: null,
      },

      fromConfig: { dateFormat: 'Y-m-d', allowInput: true },
      toConfig: { dateFormat: 'Y-m-d', allowInput: true }
    }
  },

  watch: {
    from() { this.page = 1; this.fetch(); },
    to() { this.page = 1; this.fetch(); }
  },

  mounted() {
    this.fetch();
  },

  methods: {
    async fetch() {
      this.loading = true;
      try {

        const resp = await this.axios.get(`/users/${this.$auth.user().id}/water/usage`, {
          params: {
            from: this.formatDate(this.from),
            to: this.formatDate(this.to),
            page: String(this.page),
            per_page: String(this.perPage),
            kamstrup_user_id: this.selectedKamstrupUserId || undefined,
          },
        });

        this.rows = resp.data.data || []
        this.meta = resp.data.meta || null

        if (this.meta && this.meta.selected_kamstrup_user && this.meta.selected_kamstrup_user.id) {
          this.selectedKamstrupUserId = this.meta.selected_kamstrup_user.id
        }

        this.totalPages = resp.data.pagination ? resp.data.pagination.last_page : 1
      } finally {
        this.loading = false
      }
    },

    go(p) {
      if (p < 1 || p > this.totalPages) return;
      this.page = p;
      this.fetch();
    },

    formatDate(d) {
      // d might be Date or string depending on config; normalize:
      const dt = (d instanceof Date) ? d : new Date(d);
      const yyyy = dt.getFullYear();
      const mm = String(dt.getMonth() + 1).padStart(2, "0");
      const dd = String(dt.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    },

    async openHourlyModal(day) {
      this.hourlyModal.open = true;
      this.hourlyModal.day = day;
      await this.fetchHourlyUsage();
    },

    closeHourlyModal() {
      this.hourlyModal.open = false;
    },

    async fetchHourlyUsage() {
      if (!this.hourlyModal.day) {
        return;
      }

      this.hourlyModal.loading = true;
      this.hourlyModal.rows = [];

      try {
        const resp = await this.axios.get(`/users/${this.$auth.user().id}/water/usage/hourly`, {
          params: {
            day: this.hourlyModal.day,
            kamstrup_user_id: this.selectedKamstrupUserId || undefined,
          },
        });

        const payload = resp.data && typeof resp.data === 'object' ? resp.data : {};

        this.hourlyModal.rows = payload.data || [];
        this.hourlyModal.meta = payload.meta || null;
      } finally {
        this.hourlyModal.loading = false;
      }
    },

    toNumber(value) {
      if (typeof value === 'number') {
        return Number.isFinite(value) ? value : 0;
      }

      if (typeof value !== 'string') {
        return 0;
      }

      const normalized = value.replace(',', '.').replace(/[^0-9.-]/g, '');
      const parsed = parseFloat(normalized);

      return Number.isFinite(parsed) ? parsed : 0;
    },

    formatVolume(value) {
      const rounded = Math.round(value * 1000) / 1000;
      return String(rounded);
    }
  },

  computed: {
    kamstrupUsers() {
      return this.meta && this.meta.kamstrup_users
        ? this.meta.kamstrup_users
        : []
    },
    selectedKamstrupUser() {
      return this.meta && this.meta.selected_kamstrup_user
        ? this.meta.selected_kamstrup_user
        : null
    },
    meterSerial() {
      return this.selectedKamstrupUser && this.selectedKamstrupUser.meter_serial_number
        ? this.selectedKamstrupUser.meter_serial_number
        : '—'
    },
    rangeText() {
      if (!this.meta) return ''
      return this.meta.from + ' → ' + this.meta.to
    },
    selectedHouseText() {
      if (!this.selectedKamstrupUser) return '—'
      const parts = [
        this.selectedKamstrupUser.username,
        this.selectedKamstrupUser.address,
        this.selectedKamstrupUser.city
      ].filter(Boolean)
      return parts.join(' - ')
    },
    hourlySelectedKamstrupUser() {
      return this.hourlyModal.meta && this.hourlyModal.meta.selected_kamstrup_user
        ? this.hourlyModal.meta.selected_kamstrup_user
        : null
    },
    hourlyMeterSerial() {
      return this.hourlySelectedKamstrupUser && this.hourlySelectedKamstrupUser.meter_serial_number
        ? this.hourlySelectedKamstrupUser.meter_serial_number
        : '—'
    },
    hourlySelectedHouseText() {
      if (!this.hourlySelectedKamstrupUser) return '—'

      const parts = [
        this.hourlySelectedKamstrupUser.username,
        this.hourlySelectedKamstrupUser.address,
        this.hourlySelectedKamstrupUser.city
      ].filter(Boolean)

      return parts.join(' - ')
    },
    hourlyTotalUsed() {
      const hasApiTotal = this.hourlyModal.meta
        && this.hourlyModal.meta.total_used_volume !== undefined
        && this.hourlyModal.meta.total_used_volume !== null;

      if (hasApiTotal) {
        return this.toNumber(this.hourlyModal.meta.total_used_volume);
      }

      return this.hourlyModal.rows.reduce((sum, row) => {
        return sum + this.toNumber(row.used_volume);
      }, 0);
    }
  }
};
</script>

<style lang="scss">
.zw-water-usage {
  .flatpickr-input {
    display: block !important;
  }

  .ui.input > .flatpickr-input {
    width: 100% !important;
    height: 38px;
    line-height: 38px;
  }

  .zw-water-usage-filters {
    margin-top: 1.5rem;
    margin-bottom: 1.5rem;
    padding: 1.25rem 1rem;
  }

  .zw-water-usage-filters .fields {
    margin-bottom: 0;
  }

  .zw-water-usage-filters .field label {
    margin-bottom: 0.5rem;
    font-weight: 600;
  }

  .zw-meter-select {
    width: 100%;
    min-height: 38px;
  }

  .zw-water-usage-labels {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    margin-bottom: 1.5rem;
  }

  .zw-water-usage-labels .ui.label {
    margin: 0;
    padding: 0.85rem 1rem;
  }

  .zw-day-link {
    border: 0;
    background: transparent;
    color: #2185d0;
    padding: 0;
    cursor: pointer;
    text-decoration: underline;
  }

  .ui.celled.table {
    margin-top: 1rem;
  }

  .zw-paginate {
    margin-top: 1.5rem;
  }

  .zw-water-usage-modal-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    z-index: 3000;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 1rem;
  }

  .zw-water-usage-modal {
    width: 100%;
    max-width: 980px;
    max-height: 90vh;
    overflow: auto;
  }

  .zw-water-usage-modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1rem;
  }
}
</style>



// WEBPACK FOOTER //
// src/pages/kamstrup/WaterUsageStats.vue